import axios from "axios";
import { config } from "../config";
import { getAuthHeader } from "../customAuth/authContext";
import { UpdateDocumentRequest } from "../models/requests/UpdateDocumentRequest";
import { UploadedFileRequest } from "../models/responses/UploadedFileRequest";
import { UpdateUploadedFilePageChunksOptions, UpdateUploadedFilePageChunksResponse, UploadedFilePageChunksResponse } from "../models/responses/UploadedFilePageChunkResponse";

const backendUrl = `${config.apiServerUrl}/documents`;

export const uploadFilesToCollection = async (
  files: File[],
  collectionIds: number[],
  userEmail: string,
  organizationId?: string,
) => {
  const formData = new FormData();
  formData.append('requestBody', JSON.stringify({ userEmail, collectionIds, organizationId }));
  files.forEach(file => formData.append('files', file));
  return uploadFiles(formData);
};

export const uploadFiles = async (formData: FormData) => {
  const url = `${config.pythonApiUrl}/upload-files`;
  const response = await axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: getAuthHeader()
    },
  });

  return response?.data?.result
}

/**
 * Fetch a document's bytes through our own authenticated endpoint and return a
 * local object URL for it.
 *
 * Previews go through here instead of the storage (blob) URL so that:
 *  - the storage location is never exposed to the browser,
 *  - documents kept in local storage can be previewed too,
 *  - access is authorized with the signed-in user's token.
 *
 * The document is resolved by `fileId` when we have it (documents grid), or by
 * its stored `url` (a consulted-source link rendered inside an answer).
 *
 * IMPORTANT: the caller owns the returned URL and must call
 * `URL.revokeObjectURL(...)` when done, otherwise the blob leaks for the
 * lifetime of the page.
 */
// A real uploaded-file id is a positive autoincrement value. Consulted-source
// links carry a placeholder Id of 0 (they only have a URL), so anything <= 0 is
// treated as "no id" and we resolve by URL instead.
const hasRealFileId = (fileId?: number | null): fileId is number =>
  typeof fileId === 'number' && fileId > 0;

export const fetchDocumentObjectUrl = async (
  reference: { fileId?: number | null; url?: string | null },
): Promise<string | null> => {
  const { fileId, url } = reference;
  const useFileId = hasRealFileId(fileId);
  if (!useFileId && !url) return null;

  try {
    const response = await axios.get(`${config.pythonApiUrl}/documents/content`, {
      params: useFileId ? { fileId } : { url },
      headers: { Authorization: getAuthHeader() },
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data as Blob);
  } catch (error) {
    console.error('Failed to load document content', error);
    return null;
  }
};

/**
 * Fetch a PDF rendition of a document for inline preview, through our own
 * authenticated endpoint.
 *
 * PDFs come back untouched; office documents (docx/doc/xls/...) are converted
 * server-side, so they render in the same viewer instead of being handed to a
 * third-party viewer that would need a public URL.
 *
 * Returns null when no preview can be produced (HTTP 415) so the caller can
 * fall back to a download. The caller owns the returned URL and must call
 * `URL.revokeObjectURL(...)`.
 */
export const fetchDocumentPreviewObjectUrl = async (
  reference: { fileId?: number | null; url?: string | null },
): Promise<string | null> => {
  const { fileId, url } = reference;
  const useFileId = hasRealFileId(fileId);
  if (!useFileId && !url) return null;

  try {
    const response = await axios.get(`${config.pythonApiUrl}/documents/preview`, {
      params: useFileId ? { fileId } : { url },
      headers: { Authorization: getAuthHeader() },
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data as Blob);
  } catch (error) {
    console.error('Failed to load document preview', error);
    return null;
  }
};

/**
 * Download the ORIGINAL document (not the PDF rendition) through our
 * authenticated endpoint, triggering a browser save.
 */
export const downloadDocument = async (
  reference: { fileId?: number | null; url?: string | null },
  fileName?: string,
): Promise<boolean> => {
  const objectUrl = await fetchDocumentObjectUrl(reference);
  if (!objectUrl) return false;

  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName || 'document';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser time to start the download before releasing the blob.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  return true;
};

export const getAllUploadedFiles = async () => {
  try {
    const url = `${backendUrl}/uploaded-files/all`;
    const response = await axios.get(url, {
      headers: {
        Authorization: getAuthHeader()
      }
    })
    return response.data.result
  } catch (error) {
    console.error("Error making get request", error);
    return []
  }
}

export const getFilteredFiles = async (query: UploadedFileRequest = {}) => {
  const url = `${backendUrl}/filter`;

  const params: any = {
    search: query.search || undefined,
    sourceUrl: query.sourceUrl,
    status: query.status,
    collectionIds: query.collectionIds,
    fileName: query.fileName,
    sortField: query.sortField,
    sortDirection: query.sortDirection ?? 'desc',
    page: query.page ?? 1,
    pageSize: query.pageSize ?? 10,
  };

  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (
      v === undefined || v === null ||
      (typeof v === 'number' && Number.isNaN(v)) ||
      (Array.isArray(v) && v.length === 0)
    ) delete params[k];
  });

  try {
    const response = await axios.get(url, {
      headers: { Authorization: getAuthHeader() },
      params,
      paramsSerializer: {
        serialize: (p: Record<string, any>) => {
          const s = new URLSearchParams();
          for (const [k, v] of Object.entries(p)) {
            if (Array.isArray(v)) v.forEach((x) => s.append(k, String(x)));
            else s.append(k, String(v));
          }
          return s.toString();
        },
      },
    });

    const result = response.data?.result;
    return { items: result?.items ?? [], totalCount: result?.totalCount ?? 0 };
  } catch (error) {
    console.error('Error fetching filtered files:', error);
    return { items: [], totalCount: 0 };
  }
};

export const editDocument = async (payload: UpdateDocumentRequest) => {
  const url = `${config.pythonApiUrl}/uploaded-files/edit`;
  try {
    const response = await axios.put(url, payload, {
      headers: { Authorization: getAuthHeader() },
    });
    return response?.data?.result;
  } catch (error: any) {
    console.error("Error editing doc collection:", error?.response ?? error);
    return null;
  }
};


export const deleteDocument = async (uploadedFileId: number) => {
  const url = `${config.pythonApiUrl}/uploaded-files/delete`;
  try {
    const response = await axios.delete(url, {
      headers: { Authorization: getAuthHeader() },
      data: { uploadedFileId },
    });
    return response?.data?.result;
  } catch (error: any) {
    console.error("Error deleting doc collection:", error?.response ?? error);
    return null;
  }
};

export const getUploadedFilePageChunks = async (uploadedFileId: number): Promise<UploadedFilePageChunksResponse> => {
  const url = `${config.pythonApiUrl}/uploaded-files/${uploadedFileId}/page-chunks`;
  const response = await axios.get(url, {
    headers: { Authorization: getAuthHeader() },
  });

  return response.data.result as UploadedFilePageChunksResponse;
};

export const updateUploadedFilePageChunks = async (
  uploadedFileId: number,
  pageChunks: Record<string, string>,
  options: UpdateUploadedFilePageChunksOptions = {},
): Promise<UpdateUploadedFilePageChunksResponse> => {
  const url = `${config.pythonApiUrl}/uploaded-files/${uploadedFileId}/page-chunks`;
  const response = await axios.put(
    url,
    {
      pageChunks,
      reingest: options.reingest ?? false,
    },
    { headers: { Authorization: getAuthHeader() } },
  );

  return response.data as UpdateUploadedFilePageChunksResponse;
};
